import React, { useRef, useState } from "react";
import { motion, useMotionValue, useMotionValueEvent, useTransform } from "framer-motion";
import { Check, Clock, RotateCw, Flag, Edit3, Paperclip, ArrowRight, X } from "lucide-react";
import { formatTime, formatDate, daysDiff, todayStr, taskBodyPreview, effectiveOverrides, getOccurrencesOnIso } from "@/utils";
import { SafeLinkText } from "@/components/SafeLinkText";
import { ConfettiBurst } from "@/components/ConfettiBurst";

// Contextual date badge — replaces the old {DATE pill, TDY/1D pill} pair with a single, cleaner
// label that reads like how a human would say the date. Colour is used sparingly and only for
// urgency: red for anything already past-due, amber for tomorrow's warning, everything else
// stays in a neutral zinc palette so the task TEXT is the loudest thing on the row.
//   past   → "Yesterday" / "N days overdue"  · red
//   today  → null (section header already says TODAY; time chip carries the clock)
//   +1     → "Tomorrow" · amber
//   +2…+6  → "Thu, Aug 6" · neutral
//   +7…    → "Aug 18" · neutral   (add ", 2027" if a different calendar year)
//   null   → no badge
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const contextualDate = (task) => {
    if (!task.date) return null;
    // Recurring anchors are cadence references, not due dates — the interval label carries the
    // real cadence text so we suppress the anchor entirely.
    if (task.type === "recurring") return null;
    const d = daysDiff(task.date);
    if (d === null) return null;
    const NEUTRAL = "text-zinc-300 border-zinc-500/40 bg-zinc-500/10";
    const RED = "text-red-300 border-red-500/50 bg-red-500/15";
    const AMBER = "text-amber-200 border-amber-400/50 bg-amber-500/10";
    if (d === 0) return null;                          // Today → let the section header speak
    if (d === -1) return { text: "Yesterday", cls: RED };
    if (d < -1)  return { text: `${Math.abs(d)} days overdue`, cls: RED };
    if (d === 1) return { text: "Tomorrow", cls: AMBER };
    // Parse the ISO date so we can format weekday/month locally without timezone drift.
    const [y, mo, dy] = task.date.split("-").map(Number);
    const dt = new Date(y, mo - 1, dy);
    if (d >= 2 && d <= 6) return { text: `${WEEKDAY_SHORT[dt.getDay()]}, ${MONTH_SHORT[mo - 1]} ${dy}`, cls: NEUTRAL };
    const sameYear = dt.getFullYear() === new Date().getFullYear();
    return { text: sameYear ? `${MONTH_SHORT[mo - 1]} ${dy}` : `${MONTH_SHORT[mo - 1]} ${dy}, ${dt.getFullYear()}`, cls: NEUTRAL };
};

// Ordinal suffix helper: 1 → "1st", 2 → "2nd", 13 → "13th"
const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const intervalLabel = (task) => {
    if (task.type !== "recurring") return null;
    const i = task.interval;
    // Try to derive a human phrase from anchor date + interval
    let anchor = null;
    if (task.date) {
        const [y, m, d] = task.date.split("-").map(Number);
        anchor = new Date(y, m - 1, d);
    }
    if (i === "month_end") return "End of every month";
    if (i === 1) return "Every Day";
    if (i === 7) return anchor ? `Every ${WEEKDAYS[anchor.getDay()]}` : "Every Week";
    if (i === 14) return anchor ? `Every other ${WEEKDAYS[anchor.getDay()]}` : "Every 2 Weeks";
    if (i === "month_end") return "Last day of every month";
    if (i === 30) return anchor ? `The ${ordinal(anchor.getDate())} of every month` : "Every Month";
    if (i === 31) return "Every 31 days";
    if (i === 365) return anchor ? `Every ${MONTHS[anchor.getMonth()]} ${ordinal(anchor.getDate())}` : "Every Year";
    return `Every ${i} days`;
};

/** Compact task row matching the original mEYEtasks look. Swipe right = complete, swipe left = edit.
 *  Optional props:
 *   · `onPostpone(task, fromIso, toIso)` — enables the "→" chip on recurring rows so the user can
 *     bump a single occurrence to a different day without touching the anchor/interval.
 *   · `onUndoPostpone(task, fromIso)` — reverses the postpone; when the row is rendered on its
 *     `to` day the parent should pass an `undoFromIso` so the chip can display "moved from …".
 *   · `undoFromIso` — YYYY-MM-DD label shown in the amber undo chip when this row is a
 *     postponed occurrence's target day.
 *   · `rightSlot` — free-form JSX rendered at the right of the card (e.g. "later tonight" chip
 *     for Missed rows).
 *   · `onOpenMissed(task)` — recurring rows in the Overdue section carry `_missedCount` /
 *     `_missedOccurrences` metadata; when set + non-zero, a small red pill chip renders
 *     showing "N missed · Mon" (oldest). Tapping the chip fires this callback so the caller
 *     can open MissedOccurrencesDialog to mark specific beats done. */
/** `sectionIso` — the calendar day the enclosing section already announces (Today / Tomorrow
 *  lists). A date chip that only repeats that day is noise, so it is suppressed; chips that add
 *  information (overdue days, MOVED FROM, cadence, snooze) stay. Weekly/Monthly/search omit it. */
/** `dense` — chat/inline usage: hides secondary chips (cadence label, attachment count) so the
 *  row reads time · title · state only. Overdue/MOVED FROM/missed-count still show. */
export const CompactTaskCard = ({ task, attachments, onToggle, onEdit, searchTerm, onPostpone, onUndoPostpone, undoFromIso, rightSlot, onOpenMissed, occurrenceIso, sectionIso, dense = false }) => {
    const dateChip = sectionIso && task.date === sectionIso ? null : contextualDate(task);
    const interval = dense ? null : intervalLabel(task);
    // Recurring tasks should NEVER render as crossed out — they describe an ongoing cadence,
    // not a one-shot to-do. Clicking the checkbox still updates `completed`/`completed_at` in
    // the DB (see toggleTask), but visually the card always reads as pending / upcoming.
    // However — the user needs SOME feedback that the click registered. `justChecked` flips
    // true for ~1.2s after any checkbox tap on a recurring task so the tick shows briefly.
    const [justChecked, setJustChecked] = React.useState(false);
    const isRecurring = task.type === "recurring";
    const isCurrentlyDone = isRecurring ? justChecked : !!task.completed;
    const isOverdue = !isRecurring && task.date && daysDiff(task.date) < 0 && !isCurrentlyDone;
    const attCount = dense ? 0 : (attachments || []).filter((a) => a.parent_id === task.id).length;
    const bodyPreview = taskBodyPreview(task);

    const x = useMotionValue(0);
    // Colour behind the card: neutral until threshold, then intense fuchsia (left/edit) or emerald
    // (right/complete). Emerald is used at commit-threshold so it visually reads as "check ✓".
    const bg = useTransform(
        x,
        [-100, -80, -30, 0, 30, 80, 100],
        ["#d946ef", "#a21caf", "#1a1a1a", "#121212", "#1a1a1a", "#059669", "#10b981"],
    );
    // Progress fills from 0→1 as the finger drags across the commit threshold. Powers the
    // checkmark scale + label opacity so the affordance grows in step with the gesture.
    const progressRight = useTransform(x, [0, 40, 80], [0, 0.4, 1]);
    const progressLeft = useTransform(x, [0, -40, -80], [0, 0.4, 1]);
    // The whole card scales up subtly + gains a coloured ring at commit so the moment of
    // "you've locked in" is unmistakable even when you can't hear the haptic buzz.
    const cardScale = useTransform(x, [-100, -80, 0, 80, 100], [1.02, 1.04, 1, 1.04, 1.02]);
    const cardShadow = useTransform(
        x,
        [-100, -80, 0, 80, 100],
        [
            "0 0 24px rgba(217,70,239,0.55)",
            "0 0 20px rgba(217,70,239,0.45)",
            "0 0 0px rgba(0,0,0,0)",
            "0 0 20px rgba(16,185,129,0.45)",
            "0 0 24px rgba(16,185,129,0.55)",
        ],
    );
    const swipedRef = useRef(false);
    const [pulse, setPulse] = useState(null); // 'complete' | 'edit' — brief post-commit flash
    // Fresh id on every complete-swipe so React remounts <ConfettiBurst> and replays the
    // burst even for back-to-back completions. Null = no burst mounted.
    const [confettiKey, setConfettiKey] = useState(null);
    // Track the LAST commit-crossed direction so we only fire haptic once per crossing (not on
    // every pointer move). vibrateOnce debounces the actual navigator.vibrate call.
    const lastCrossedRef = useRef(0); // -1 = crossed left, +1 = crossed right, 0 = below both thresholds
    const vibrateOnce = (pattern) => {
        try { navigator.vibrate?.(pattern); } catch (_) { /* not available on desktop / some browsers */ }
    };
    useMotionValueEvent(x, "change", (v) => {
        let next = 0;
        if (v >= 80) next = 1;
        else if (v <= -80) next = -1;
        if (next !== lastCrossedRef.current) {
            // Only vibrate when we CROSS into a commit zone, not when leaving it.
            if (next !== 0) vibrateOnce(15); // 15ms short crisp tick
            lastCrossedRef.current = next;
        }
    });

    const onDragEnd = (_, info) => {
        if (info.offset.x > 80) {
            swipedRef.current = true;
            // Success pulse — brief scale-out with an emerald ring around the whole row so the
            // eye confirms the swipe registered before the row disappears from Today/Overdue.
            setPulse("complete");
            // 🎉 CELEBRATE — mount the confetti burst and fire a longer haptic pattern that
            // reads as a "yes!" instead of a plain tick.
            setConfettiKey(Date.now());
            vibrateOnce([25, 30, 15, 30, 25]); // celebratory triple-pulse
            setTimeout(() => setPulse(null), 400);
            setTimeout(() => setConfettiKey(null), 850); // clean up after animation
            onToggle?.(task, occurrenceIso);
        } else if (info.offset.x < -80) {
            swipedRef.current = true;
            setPulse("edit");
            vibrateOnce(20);
            setTimeout(() => setPulse(null), 400);
            onEdit?.(task);
        }
        x.set(0);
        lastCrossedRef.current = 0;
        setTimeout(() => (swipedRef.current = false), 200);
    };

    return (
        <div className="relative" data-testid={`compact-task-${task.id}`} data-occurrence={occurrenceIso || task._occKey || undefined}>
            {/* Confetti — mounts only while a fresh completion is celebrating. Rendered ABOVE
                the card via absolute+z so the particles fly out past the row edges. */}
            {confettiKey !== null && (
                <div className="absolute inset-0 z-20" aria-hidden="true">
                    <ConfettiBurst keyId={confettiKey} size={220} />
                </div>
            )}
            {/* swipe background — the labels + icons scale + brighten as the swipe crosses commit */}
            <motion.div
                className="absolute inset-0 rounded-lg flex items-center justify-between px-4 text-black font-brand font-bold uppercase text-[10px] tracking-widest overflow-hidden"
                style={{ background: bg }}
                aria-hidden="true"
            >
                <motion.div
                    className="flex items-center gap-1.5"
                    style={{
                        opacity: progressRight,
                        scale: useTransform(progressRight, [0, 1], [0.8, 1.15]),
                    }}
                >
                    <motion.span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/25"
                        style={{ scale: useTransform(progressRight, [0, 1], [0.6, 1.3]) }}
                    >
                        <Check size={14} strokeWidth={3.5} />
                    </motion.span>
                    complete
                </motion.div>
                <motion.div
                    className="flex items-center gap-1.5"
                    style={{
                        opacity: progressLeft,
                        scale: useTransform(progressLeft, [0, 1], [0.8, 1.15]),
                    }}
                >
                    edit
                    <motion.span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/25"
                        style={{ scale: useTransform(progressLeft, [0, 1], [0.6, 1.3]) }}
                    >
                        <Edit3 size={13} strokeWidth={2.5} />
                    </motion.span>
                </motion.div>
            </motion.div>

            <motion.div
                drag="x"
                dragConstraints={{ left: -140, right: 140 }}
                dragElastic={0.15}
                style={{ x, scale: cardScale, boxShadow: cardShadow }}
                onDragEnd={onDragEnd}
                onClick={() => !swipedRef.current && onEdit?.(task)}
                animate={
                    pulse === "complete"
                        ? { boxShadow: ["0 0 0 rgba(16,185,129,0)", "0 0 24px rgba(16,185,129,0.9)", "0 0 0 rgba(16,185,129,0)"] }
                        : pulse === "edit"
                        ? { boxShadow: ["0 0 0 rgba(217,70,239,0)", "0 0 24px rgba(217,70,239,0.9)", "0 0 0 rgba(217,70,239,0)"] }
                        : undefined
                }
                transition={{ duration: 0.4, ease: "easeOut" }}
                className={`relative z-10 rounded-lg border ${isOverdue ? "border-red-500/25" : "border-meye-border"} bg-meye-card/80 backdrop-blur-sm px-3 py-2 flex items-center gap-2 cursor-pointer hover:border-meye-cyan/50 transition-colors select-none touch-pan-y`}
            >
                {/* checkbox */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        // Give recurring tasks a brief green-tick pulse so the user KNOWS the
                        // check-in registered. The card intentionally reverts to "pending" after
                        // because recurring items describe an ongoing cadence — see comment on
                        // `isCurrentlyDone` above.
                        if (isRecurring) {
                            setJustChecked(true);
                            setTimeout(() => setJustChecked(false), 1200);
                        }
                        // `false` = the check-in was refused (e.g. future obligation) — drop the tick at once.
                        Promise.resolve(onToggle?.(task, occurrenceIso)).then((r) => { if (r === false) setJustChecked(false); }).catch(() => setJustChecked(false));
                    }}
                    className={`flex-shrink-0 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                        isCurrentlyDone
                            ? "bg-emerald-500 border-emerald-500 text-black"
                            : "border-zinc-600 hover:border-meye-cyan"
                    }`}
                    data-testid={`compact-task-toggle-${task.id}`}
                    data-occurrence={occurrenceIso || task._occKey || undefined}
                >
                    {isCurrentlyDone && <Check size={12} strokeWidth={3} />}
                </button>

                {/* Order: TIME (if present) → contextual date chip → title. Only ONE date-related
                     badge now — see `contextualDate` for the full mapping (Yesterday, Tomorrow,
                     "Thu, Aug 6", …). Colour is reserved for urgency; neutral otherwise. */}
                {task.time && (
                    <span className="text-[10px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-violet-500/40 bg-violet-500/10 text-violet-300 flex items-center gap-1 flex-shrink-0">
                        <Clock size={9} strokeWidth={2} />
                        {formatTime(task.time)}
                    </span>
                )}

                {dateChip && (
                    <span
                        className={`text-[10px] font-brand font-bold tracking-wide px-2 py-0.5 rounded border flex-shrink-0 ${dateChip.cls}`}
                    >
                        {dateChip.text}
                    </span>
                )}

                {task.type === "recurring" && !task.time && (
                    <RotateCw size={12} strokeWidth={2} className="text-blue-400 flex-shrink-0" />
                )}

                {/* Priority flag — red=high, cyan=med (default), fuchsia=low. Null/undefined
                     reads as medium so legacy tasks show a neutral cyan flag. Someday view
                     sorts by this priority; other views just display it. */}
                {(() => {
                    const p = task.priority || "med";
                    const cls = p === "high" ? "text-red-400" : p === "low" ? "text-meye-fuchsia" : "text-meye-cyan";
                    const label = p === "high" ? "High priority" : p === "low" ? "Low priority" : "Medium priority";
                    return (
                        <Flag
                            size={13}
                            strokeWidth={2}
                            className={`${cls} flex-shrink-0`}
                            data-testid={`compact-task-priority-${task.id}-${p}`}
                            aria-label={label}
                        />
                    );
                })()}

                <span className="flex-1 min-w-0 flex flex-col">
                    <span
                        className={`text-base truncate ${isCurrentlyDone ? "line-through text-zinc-500" : "text-zinc-100"} ${bodyPreview ? "font-semibold" : ""}`}
                        data-testid={`compact-task-title-${task.id}`}
                    >
                        <SafeLinkText text={task.text} term={searchTerm} />
                    </span>
                    {bodyPreview && (
                        <span
                            className="text-xs text-zinc-500 leading-snug line-clamp-2 break-words"
                            data-testid={`compact-task-body-${task.id}`}
                        >
                            <SafeLinkText text={bodyPreview} term={searchTerm} />
                        </span>
                    )}
                </span>

                {attCount > 0 && (
                    <span
                        className="flex items-center gap-0.5 text-[10px] font-brand font-bold px-1.5 py-0.5 rounded-md border border-meye-cyan/40 bg-meye-cyan/10 text-meye-cyan flex-shrink-0"
                        title={`${attCount} attachment${attCount === 1 ? "" : "s"}`}
                        data-testid={`compact-task-attachment-badge-${task.id}`}
                    >
                        <Paperclip size={10} strokeWidth={2} />
                        {attCount > 1 && <span>{attCount}</span>}
                    </span>
                )}

                {interval && (
                    <span className="hidden sm:inline text-[10px] font-brand font-medium px-2 py-0.5 rounded-md bg-blue-500/15 text-blue-300 border border-blue-500/25 flex-shrink-0">
                        {interval}
                    </span>
                )}

                {/* Postpone controls — recurring tasks only. Two mutually-exclusive UI modes:
                     · If the row is a postponement's TARGET day (`undoFromIso` set), show an
                       amber "moved from N" chip with an X to un-postpone.
                     · Otherwise render the "→" bump button — tapping expands to native date
                       picker + shortcut chips.
                     Both stop event propagation so the outer row's edit-on-click is undisturbed. */}
                {task.type === "recurring" && undoFromIso && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onUndoPostpone?.(task, undoFromIso); }}
                        className="flex items-center gap-1 text-[9px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-400/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/25 flex-shrink-0"
                        title={onUndoPostpone ? `Un-postpone (moved from ${undoFromIso})` : `Moved from ${undoFromIso}`}
                        data-testid={`compact-task-moved-from-${task.id}-${undoFromIso}`}
                    >
                        moved from {formatDate(undoFromIso)} {onUndoPostpone && <X size={9} strokeWidth={2.5} />}
                    </button>
                )}
                {task.type === "recurring" && !undoFromIso && onPostpone && (
                    <PostponeButton task={task} onPostpone={onPostpone} occurrenceIso={occurrenceIso || task._occKey} />
                )}
                {rightSlot}
                {task._missedCount > 0 && onOpenMissed && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onOpenMissed(task); }}
                        className="flex items-center gap-1 text-[9px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-red-500/50 bg-red-500/15 text-red-200 hover:bg-red-500/30 flex-shrink-0"
                        title={`${task._missedCount} outstanding · oldest ${task._oldestMissedIso}`}
                        data-testid={`compact-task-missed-count-${task.id}`}
                    >
                        {task._missedCount} missed
                    </button>
                )}
            </motion.div>
        </div>
    );
};

/** Bumps a single recurring occurrence to a different day.
 *  Emits `onPostpone(task, fromIso, toIso)` where fromIso is the NEXT scheduled occurrence
 *  (computed from anchor + interval, honoring existing overrides). Presents 4 quick offsets
 *  plus a native date picker for arbitrary targets. */
const PostponeButton = ({ task, onPostpone, occurrenceIso }) => {
    const [open, setOpen] = useState(false);
    // SOURCE key of the obligation this row shows: given by the parent, else the next obligation
    // from today resolved through the ONE recurrence engine (getOccurrencesOnIso) — no local math.
    const fromIso = React.useMemo(() => {
        if (occurrenceIso) return occurrenceIso;
        if (!task.interval) return task.date || todayStr();
        const d = new Date(); d.setHours(0, 0, 0, 0);
        for (let off = 0; off < 400; off++) {
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            const occs = getOccurrencesOnIso(task, iso).filter((o) => !(task.completions || []).includes(o.key));
            if (occs.length) return occs[0].key;
            d.setDate(d.getDate() + 1);
        }
        return todayStr();
    }, [task, occurrenceIso]);
    // Offsets are relative to where the beat currently SITS (its display day), not its source day.
    const displayIso = React.useMemo(() => effectiveOverrides(task).find((o) => o.from === fromIso)?.to || fromIso, [task, fromIso]);

    const isoAtOffset = (offsetDays) => {
        const [yy, mm, dd] = displayIso.split("-").map(Number);
        const d = new Date(yy, mm - 1, dd);
        d.setDate(d.getDate() + offsetDays);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    const bump = (toIso) => {
        // A date-only move keeps the obligation's CURRENT effective time (its to_time, if moved
        // before) — never silently reverts to the series base time.
        const occ = getOccurrencesOnIso(task, displayIso).find((o) => o.key === fromIso);
        const keepTime = occ?.time && occ.time !== task.time ? occ.time : null;
        onPostpone?.(task, fromIso, toIso, keepTime, "quick_bump");
        setOpen(false);
    };

    if (!open) {
        return (
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                className="p-1 rounded border border-meye-border bg-black/30 text-zinc-400 hover:text-meye-cyan hover:border-meye-cyan flex-shrink-0"
                title="Postpone this occurrence"
                data-testid={`compact-task-postpone-${task.id}`}
            >
                <ArrowRight size={11} strokeWidth={2.5} />
            </button>
        );
    }
    return (
        <div
            className="flex items-center gap-1 rounded-md border border-meye-cyan/50 bg-black/60 px-1.5 py-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
            data-testid={`compact-task-postpone-menu-${task.id}`}
        >
            {[
                ["+1", 1],
                ["+3", 3],
                ["+7", 7],
            ].map(([label, off]) => (
                <button
                    key={label}
                    onClick={() => bump(isoAtOffset(off))}
                    className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border border-meye-cyan/40 bg-meye-cyan/10 text-meye-cyan hover:bg-meye-cyan/25"
                    data-testid={`compact-task-postpone-${task.id}-${label}`}
                >
                    {label}d
                </button>
            ))}
            <input
                type="date"
                min={isoAtOffset(1)}
                onChange={(e) => e.target.value && bump(e.target.value)}
                className="text-[10px] font-mono bg-black/50 border border-meye-border rounded px-1 py-0.5 text-zinc-200 w-24"
                data-testid={`compact-task-postpone-${task.id}-picker`}
            />
            <button
                onClick={() => setOpen(false)}
                className="p-0.5 text-zinc-500 hover:text-red-400"
                data-testid={`compact-task-postpone-${task.id}-cancel`}
            >
                <X size={11} strokeWidth={2.5} />
            </button>
        </div>
    );
};

export default CompactTaskCard;
